import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Boxes, AlertTriangle, Warehouse, ShoppingCart } from "lucide-react";
import {
  mockStockItems,
  mockAssets,
  mockProcurement,
} from "@project/lib/mock-modules2";

export default function InventoryDashboard() {
  const totalSku = mockStockItems.length;
  const lowStock = mockStockItems.filter(s => s.status === "Low");
  const totalAssets = mockAssets.length;
  const procurementOpen = mockProcurement.filter(
    p => p.status !== "Delivered"
  ).length;
  const procurementValue = mockProcurement.reduce((s, p) => s + p.amount, 0);

  return (
    <AdminShell
      title="Inventory & Assets"
      subtitle="Stock, asset register & procurement at a glance"
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Stock SKUs"
          value={totalSku}
          icon={<Boxes className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub="Across kitchen, pharmacy, housekeeping"
        />
        <StatCard
          label="Low Stock Alerts"
          value={lowStock.length}
          icon={<AlertTriangle className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub="Below reorder level"
        />
        <StatCard
          label="Assets Tracked"
          value={totalAssets}
          icon={<Warehouse className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub="Vehicles, medical, utility"
        />
        <StatCard
          label="Open Procurement"
          value={procurementOpen}
          icon={<ShoppingCart className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={`₹${procurementValue.toLocaleString("en-IN")} in pipeline`}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Stock Summary"
          subtitle="Current quantities by item"
          className="lg:col-span-2"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Reorder</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockStockItems.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.category}</TableCell>
                    <TableCell>
                      {s.stock} {s.unit}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.reorder} {s.unit}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          s.status === "Low" ? "destructive" : "secondary"
                        }
                      >
                        {s.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>

        <SectionCard title="Low Stock Items" subtitle="Order before depletion">
          <ul className="space-y-3">
            {lowStock.map(s => (
              <li key={s.id} className="flex items-start gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {s.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {s.stock} {s.unit} left · reorder at {s.reorder} {s.unit}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Assets Overview"
          subtitle="Vehicles, medical & utility"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockAssets.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell>{a.category}</TableCell>
                    <TableCell>{a.location}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          a.status === "Maintenance"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {a.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>

        <SectionCard
          title="Procurement Snapshot"
          subtitle="Latest purchase orders"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockProcurement.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.id}</TableCell>
                    <TableCell>
                      <div>{p.item}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {p.vendor} · {p.when}
                      </div>
                    </TableCell>
                    <TableCell>₹{p.amount.toLocaleString("en-IN")}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{p.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
